import React, { useContext, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import classNames from 'classnames'
import { ButtonPrimary } from 'front-end/src/components'
import { ManageCampaignContext } from '../ManageCampaignPage'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { Link } from 'react-router-dom'

import { notification } from '../../../components/common/notifications/Notifications'
import { apolloClientPrivate } from '../../../graphql/apolloClient'

import humanizeDuration from 'humanize-duration'
import { isCampaignFixed, isCampaignImmediate } from '../utils'
import {
  CampaignMessageTypeEnum,
  CampaignScheduleEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  FixedActionsComingUpInActivationWindowQuery,
  FixedActionsComingUpInActivationWindowQueryVariables,
  FixedActionsComingUpInActivationWindowDocument
} from '../ManageCampaignPage.codegen'
import {
  useActivateCampaignMutation,
  useOrganisationNotificationsQuery
} from './CampaignReadinessHero.codegen'

export function showCampaignActivationUpcomingActionsError(campaignData: {
  activationWindowDuration: number
}) {
  notification.error(
    t`This campaign must be activated at least ${humanizeDuration(
      campaignData.activationWindowDuration
    )} before first scheduled action of this campaign. Please reschedule your actions.`
  )
}

const CampaignReadinessHero = () => {
  const { refetchCampaign, campaign, loading } = useContext(
    ManageCampaignContext
  )
  const {
    canBeActivated,
    hasCorrectActionScheduleTypes,
    wasCampaignDeactivated
  } = campaign

  const [activateCampaign, { loading: activatingCampaign }] =
    useActivateCampaignMutation({
      variables: { id: campaign.id, active: true }
    })
  const [checkingUpcomingFixedActions, setCheckingUpcomingFixedActions] =
    useState(false)

  const { data, loading: queryLoading } = useOrganisationNotificationsQuery()

  if (queryLoading) {
    return null
  }

  const notifications = data?.admin?.organisation?.notifications

  const hasCampaignSomeActions = (): boolean => {
    return (
      campaign.emailActions.length +
        campaign.banners.length +
        campaign.pathwayShareActions.length +
        campaign.certificationShareActions.length +
        campaign.topicShareActions.length +
        campaign.surveyShareActions.length +
        campaign.messageActions.length >
      0
    )
  }

  const hasBannerActions = (): boolean => {
    return campaign.banners.length > 0
  }

  const hasSMSMessageActions = (): boolean => {
    const { messageActions } = campaign
    return !!messageActions.find(
      (messageAction) => messageAction.type === CampaignMessageTypeEnum.SMS
    )
  }

  const hasSlackMessageActions = (): boolean => {
    const { messageActions } = campaign
    return !!messageActions.find(
      (messageAction) => messageAction.type === CampaignMessageTypeEnum.SLACK
    )
  }

  const hasMsTeamsMessageActions = (): boolean => {
    const { messageActions } = campaign
    return !!messageActions.find(
      (messageAction) => messageAction.type === CampaignMessageTypeEnum.MSTEAMS
    )
  }

  const isSMSMessagingEnabled = (): boolean =>
    notifications && !!notifications.sms

  const isSlackIntegrationEnabled = (): boolean =>
    notifications && !!notifications.slack.slack_access_token

  const isMsTeamsIntegrationEnabled = (): boolean =>
    notifications && !!notifications.msteams

  const renderAllowedActions = () => {
    const boldBanner = !isCampaignImmediate(campaign) && (
      <Trans>
        <b>banner</b>,
      </Trans>
    )

    return (
      <Trans>
        Add at least one of those: <b>email</b>, {boldBanner}{' '}
        <b>resource share</b>, <b>pathway share</b>, <b>message</b>,{' '}
        <b>survey share</b>
      </Trans>
    )
  }

  if (loading) {
    return null
  }

  const CAMPAIGN_TYPES_TRANSLATIONS = {
    [CampaignScheduleEnum.IMMEDIATE]: t`IMMEDIATE`,
    [CampaignScheduleEnum.FIXED]: t`FIXED`,
    [CampaignScheduleEnum.RELATIVE]: t`RELATIVE`
  }

  return (
    <>
      {canBeActivated && !campaign.is_active && (
        <div className="CampaignDetailSubpage__hero">
          <h2>
            <Trans>Your campaign is ready to go live!</Trans>
          </h2>
          <ButtonPrimary
            isLoading={activatingCampaign || checkingUpcomingFixedActions}
            onClick={async () => {
              if (isCampaignFixed(campaign)) {
                setCheckingUpcomingFixedActions(true)
                let campaignData
                try {
                  // lazy query or a query with skip couldn't be used here unfortunately, apollo client sucks: https://github.com/apollographql/react-apollo/issues/3499
                  const res = await apolloClientPrivate.mutate<
                    FixedActionsComingUpInActivationWindowQuery,
                    FixedActionsComingUpInActivationWindowQueryVariables
                  >({
                    mutation: FixedActionsComingUpInActivationWindowDocument,
                    variables: { id: campaign.id }
                  })

                  campaignData = res?.data?.admin?.campaign
                } finally {
                  setCheckingUpcomingFixedActions(false)
                }

                if (
                  campaignData.fixedActionsComingUpInActivationWindow.length > 0
                ) {
                  showCampaignActivationUpcomingActionsError(campaignData)
                  return
                }
              }

              await activateCampaign()
              refetchCampaign()
            }}
          >
            <Trans>Activate this campaign</Trans>
          </ButtonPrimary>
        </div>
      )}

      {!canBeActivated && !campaign.is_active && !wasCampaignDeactivated && (
        <div className="CampaignDetailSubpage__hero campaign-not-ready">
          <h2>
            <Trans>Your campaign is not yet ready to go live.</Trans>
          </h2>
          <p>
            <Trans>In order to launch your campaign, you must:</Trans>
          </p>
          <ul className="CampaignSteps">
            <li
              className={classNames({
                completed: campaign.filters.length > 0
              })}
            >
              <Trans>Define your audience</Trans>
            </li>
            <li
              className={classNames({
                completed: hasCampaignSomeActions()
              })}
            >
              {renderAllowedActions()}
            </li>
            <li
              className={classNames({
                completed:
                  hasCorrectActionScheduleTypes && hasCampaignSomeActions()
              })}
            >
              <Trans>
                Make sure that all actions use{' '}
                <b>{CAMPAIGN_TYPES_TRANSLATIONS[campaign.schedule_type]}</b>{' '}
                schedule type
              </Trans>
            </li>
            {campaign.schedule_type === CampaignScheduleEnum.IMMEDIATE && (
              <li
                className={classNames({
                  completed: !hasBannerActions()
                })}
              >
                <Trans>
                  Make sure that immediate campaign does not contain any banner
                  action
                </Trans>
              </li>
            )}
            {campaign.schedule_type === CampaignScheduleEnum.FIXED && (
              <li
                className={classNames({
                  completed: campaign.actionsInThePastCount === 0
                })}
              >
                <Trans>
                  Make sure that all actions are scheduled at least 5 minutes
                  from now
                </Trans>
              </li>
            )}
            {hasSMSMessageActions() && (
              <li
                className={classNames({
                  completed: isSMSMessagingEnabled()
                })}
              >
                <Trans>
                  Make sure <b>SMS messaging</b> is enabled in Account Settings
                  Notifications
                </Trans>
              </li>
            )}
            {hasSlackMessageActions() && (
              <li
                className={classNames({
                  completed: isSlackIntegrationEnabled()
                })}
              >
                <Trans>
                  Make sure <b>Slack integration</b> is enabled in Account
                  Settings Notifications
                </Trans>
              </li>
            )}
            {hasMsTeamsMessageActions() && (
              <li
                className={classNames({
                  completed: isMsTeamsIntegrationEnabled()
                })}
              >
                <Trans>
                  Make sure <b>Microsoft Teams integration</b> is enabled in
                  Account Settings Notifications
                </Trans>
              </li>
            )}
          </ul>
        </div>
      )}

      {!campaign.is_active && wasCampaignDeactivated && (
        <div className="CampaignDetailSubpage__hero campaign-not-ready">
          <h2>
            <Trans>Deactivated campaign</Trans>
          </h2>
          <p>
            <Trans>
              This campaign was deactivated and can not be activated again, all
              aditional changes are not allowed anymore.
            </Trans>
          </p>
          <p>
            <Trans>
              In order to launch campaign with this setup, please copy this
              campaign in
            </Trans>{' '}
            <Link to={linkBuilder.campaigns()}>
              <Trans>campaigns overview</Trans>
            </Link>
            .
          </p>
        </div>
      )}
    </>
  )
}

export default CampaignReadinessHero
